import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  getReportFormSettings,
  NERIS_GROUP_TYPE_LIST,
  saveFormSettings,
  setFormSettings,
} from '../../store/nerisSettingsReducer';
import { useSelector } from 'react-redux';
import { NestedGroupList } from './Settings';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter';
import { FormContext } from './FormContext';
import { getItemInObjectByPath, makeObjectFromPath } from './utils';
import { debounce } from 'lodash';

const updateObject = (clonedSettings, fieldPath, value) => {
  const pathParts = fieldPath.split('.');
  const parentPath = pathParts.slice(0, -1).join('.'); // "parent.child"
  const lastElement = pathParts.at(-1);

  const parentObject = getItemInObjectByPath(clonedSettings, parentPath);

  parentObject[lastElement] = value;
};

const NerisReportForm = () => {
  const { formSettings, loadingFormSettings } = useSelector(
    (state) => state.nerisSettingsReducer
  );

  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    dispatch(getReportFormSettings(NERIS_GROUP_TYPE_LIST[tabValue]));
  }, [dispatch, tabValue]);

  const saveSettingsHandler = useCallback(
    debounce((data) => {
      dispatch(saveFormSettings(data));
    }, 500),
    [dispatch]
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const nerisList = useMemo(() => {
    if (!formSettings) {
      return [];
    }

    return Object.entries(formSettings);
  }, [formSettings]);

  /**
   * Updates form settings. Supports two modes:
   * 1. Single change: onChange('path', 'newValue')
   * 2. Multiple changes: onChange([['path1', 'value1'], ['path2', 'value2']])
   *
   * @param {string|Array} fieldPathOrChanges - Field path (string) or array of changes
   * @param {any} value - New value (used only for single change)
   */
  const onChange = useCallback(
    (...args) => {
      //todo change logic to destructuring
      // it will halp with performance
      const clonedSettings = structuredClone(formSettings);

      // for saving to back
      let objForPartialSave;

      if (args.length === 1) {
        args[0].forEach(([fieldPath, value]) => {
          updateObject(clonedSettings, fieldPath, value);
        });

        objForPartialSave = args[0].reduce(
          (acc, [fieldPath, value]) =>
            makeObjectFromPath(fieldPath, value, acc),
          {}
        );
      } else {
        updateObject(
          clonedSettings,
          args[0], //fieldPath
          args[1] // value
        );

        objForPartialSave = makeObjectFromPath(args[0], args[1]);
      }

      dispatch(setFormSettings(clonedSettings));
      saveSettingsHandler(objForPartialSave);
    },
    [formSettings]
  );

  return (
    <FormContext.Provider
      value={{
        onChange,
      }}
    >
      <Card
        className="p-3 mt-4"
        sx={{
          boxShadow: '0px 4px 4px 0px #0000000F',
        }}
      >
        <Stack spacing={3}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="navigation"
          >
            {NERIS_GROUP_TYPE_LIST.map((nerisName) => {
              return (
                <Tab
                  key={nerisName}
                  label={capitalizeFirstLetter(nerisName)}
                  variant="navigation"
                />
              );
            })}
          </Tabs>

          {/*loading*/}
          {loadingFormSettings && (
            <Box
              sx={{ minHeight: '100px' }}
              className="w-100 h-100 d-flex justify-content-center align-items-center"
            >
              <CircularProgress />
            </Box>
          )}
          {/* render data*/}
          {!loadingFormSettings &&
            nerisList.map(([nerisName, nerisGroup]) => (
              <Fragment key={nerisName}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {capitalizeFirstLetter(nerisName)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    maxWidth: '900px',
                  }}
                >
                  {nerisGroup && (
                    <NestedGroupList
                      groupList={nerisGroup}
                      parentPath={nerisName}
                    />
                  )}
                </Box>
              </Fragment>
            ))}
        </Stack>
      </Card>
    </FormContext.Provider>
  );
};

export default NerisReportForm;
